import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { FiSun, FiMoon, FiMonitor } from 'react-icons/fi';

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ $active, theme }) => 
    $active ? theme.colors.primary : theme.colors.backgroundTertiary};
  color: ${({ $active, theme }) => 
    $active ? '#ffffff' : theme.colors.textSecondary};
  
  &:hover {
    background: ${({ $active, theme }) => 
      $active ? theme.colors.primaryDark : theme.colors.border};
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const QuickToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  color: ${({ theme }) => theme.colors.text};
  
  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Label = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-right: 8px;
`;

// Simple toggle button for header
export const ThemeQuickToggle = () => {
  const { toggleTheme, isDark } = useTheme();
  
  return (
    <QuickToggle onClick={toggleTheme} title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
      {isDark ? <FiSun /> : <FiMoon />}
    </QuickToggle>
  );
};

// Full theme selector with light/dark/system options
export const ThemeSelector = ({ showLabel = true }) => {
  const { themePreference, setTheme } = useTheme();
  
  return (
    <ToggleContainer>
      {showLabel && <Label>Theme:</Label>}
      <ToggleButton 
        $active={themePreference === 'light'}
        onClick={() => setTheme('light')}
        title="Light mode"
      >
        <FiSun />
      </ToggleButton>
      <ToggleButton 
        $active={themePreference === 'dark'}
        onClick={() => setTheme('dark')}
        title="Dark mode"
      >
        <FiMoon />
      </ToggleButton>
      <ToggleButton 
        $active={themePreference === 'system'}
        onClick={() => setTheme('system')}
        title="System preference"
      >
        <FiMonitor />
      </ToggleButton>
    </ToggleContainer>
  );
};

// Dropdown style selector
const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  min-width: 160px;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  z-index: 100;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: ${({ $active, theme }) => 
    $active ? theme.colors.primaryLight : 'transparent'};
  color: ${({ $active, theme }) => 
    $active ? theme.colors.primary : theme.colors.text};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  
  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const ThemeDropdown = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { themePreference, setTheme, isDark } = useTheme();
  const dropdownRef = React.useRef(null);
  
  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const getCurrentIcon = () => {
    switch (themePreference) {
      case 'light': return <FiSun />;
      case 'dark': return <FiMoon />;
      default: return <FiMonitor />;
    }
  };
  
  const getCurrentLabel = () => {
    switch (themePreference) {
      case 'light': return 'Light';
      case 'dark': return 'Dark';
      default: return 'System';
    }
  };
  
  const handleSelect = (theme) => {
    setTheme(theme);
    setIsOpen(false);
  };
  
  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton onClick={() => setIsOpen(!isOpen)}>
        {getCurrentIcon()}
        <span>{getCurrentLabel()}</span>
      </DropdownButton>
      
      {isOpen && (
        <DropdownMenu>
          <DropdownItem 
            $active={themePreference === 'light'}
            onClick={() => handleSelect('light')}
          >
            <FiSun /> Light
          </DropdownItem>
          <DropdownItem 
            $active={themePreference === 'dark'}
            onClick={() => handleSelect('dark')}
          >
            <FiMoon /> Dark
          </DropdownItem>
          <DropdownItem 
            $active={themePreference === 'system'}
            onClick={() => handleSelect('system')}
          >
            <FiMonitor /> System
          </DropdownItem>
        </DropdownMenu>
      )}
    </DropdownContainer>
  );
};

export default ThemeSelector;
